// PRD.279 — Elia AI voice dictation hook.
//
// Drives MediaRecorder for a single, ephemeral dictation bound to the active
// Elia context key. All decision logic lives in the pure `elia-voice-dictation`
// module; this hook is the browser-glue: getUserMedia, timers, AbortController,
// a generation counter to reject stale callbacks, and ONE idempotent cleanup.
//
// Privacy (PRD.279 "Audio lifecycle"): audio lives only as in-memory chunks for
// the active operation. No blob is retained, no object URL is created, nothing
// is persisted, and no patient/visit id or transcript is ever logged.

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { getIdToken } from '@helios/auth-firebase-web';

import {
  MAX_RECORDING_DURATION_S,
  transcribeAudioBlob,
  type VttMode,
} from '../../voice/vtt-support';
import {
  blobToBase64,
  isRecordingSupported,
  pickSupportedMimeType,
} from '../../voice/vtt-recording';
import {
  ELIA_VOICE_IDLE,
  canStartDictation,
  contextChanged,
  eliaVoiceReducer,
  isDictationAvailable,
  isResultStale,
  type EliaVoiceState,
  type EliaVoiceStatus,
} from './elia-voice-dictation';

/** Elia dictation normalizes speech to English before it lands in the composer. */
const DICTATION_MODE: VttMode = 'translation';
const VTT_URL = import.meta.env.VITE_VOICE_TO_TEXT_URL;

export type EliaVoiceLogEvent =
  | 'recording_started'
  | 'recording_cancelled'
  | 'recording_stopped'
  | 'max_duration_reached'
  | 'transcription_started'
  | 'transcription_completed'
  | 'transcription_failed'
  | 'context_changed_cancelled'
  | 'recording_failed'
  | 'unsupported';

/** Safe structured log — event + non-PHI metadata only (never transcript/ids). */
function safeLog(event: EliaVoiceLogEvent, meta?: Record<string, string | number>): void {
  console.info('[elia-voice]', event, meta ?? {});
}

export interface UseEliaVoiceDictationArgs {
  /** The effective Elia context key (`patient:…` / `visit:…:…`) — kept current. */
  contextKey: string;
  /** An Elia message send is currently in flight (disables the mic). */
  sending: boolean;
  /** Append a successful transcript into the composer draft. */
  onInsertTranscript: (transcript: string) => void;
}

export interface UseEliaVoiceDictationReturn {
  status: EliaVoiceStatus;
  error: string | null;
  /** Seconds elapsed since recording started (ticks every second). */
  elapsed: number;
  maxDuration: number;
  /** Browser can record audio. */
  supported: boolean;
  /** Mic control should be shown at all (supported + backend configured). */
  available: boolean;
  /** Mic may begin a new recording right now. */
  canStart: boolean;
  start: () => void;
  /** Stop recording and transcribe. */
  stop: () => void;
  /** Discard the recording without transcribing; restore the draft. */
  cancel: () => void;
  dismissError: () => void;
}

export function useEliaVoiceDictation(
  args: UseEliaVoiceDictationArgs,
): UseEliaVoiceDictationReturn {
  const [state, dispatch] = useReducer(eliaVoiceReducer, ELIA_VOICE_IDLE);
  const [elapsed, setElapsed] = useState(0);

  const supported = useState(() => isRecordingSupported())[0];
  const configured = !!VTT_URL;
  const available = isDictationAvailable({ supported, configured });

  // ── Refs (imperative machinery; never trigger renders) ──
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoStopRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const mimeTypeRef = useRef<string>('audio/webm');
  /** Bumped whenever an operation is superseded (start/cancel/context/unmount). */
  const generationRef = useRef(0);
  /** Context key captured at record start, for a post-await cross-context check. */
  const recordStartKeyRef = useRef<string>('');
  const stopRef = useRef<() => void>(() => {});

  // Keep latest props/state readable inside timers and async callbacks without
  // widening useCallback deps (which would re-create handlers every render).
  const contextKeyRef = useRef(args.contextKey);
  contextKeyRef.current = args.contextKey;
  const sendingRef = useRef(args.sending);
  sendingRef.current = args.sending;
  const onInsertRef = useRef(args.onInsertTranscript);
  onInsertRef.current = args.onInsertTranscript;
  const stateRef = useRef<EliaVoiceState>(state);
  stateRef.current = state;

  // ── The single idempotent cleanup (PRD.279 FR-7) ──
  const releaseResources = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (autoStopRef.current) {
      clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    setElapsed(0);
  }, []);

  /** Cleanup AND invalidate any in-flight async result (bumps the generation). */
  const invalidate = useCallback(() => {
    generationRef.current += 1;
    releaseResources();
  }, [releaseResources]);

  const start = useCallback(() => {
    const s = stateRef.current.status;
    if (s !== 'idle' && s !== 'error') return; // never interrupt a live op
    if (!supported) {
      dispatch({ type: 'UNSUPPORTED' });
      safeLog('unsupported');
      return;
    }
    const mime = pickSupportedMimeType();
    if (!mime) {
      dispatch({ type: 'UNSUPPORTED' });
      safeLog('unsupported');
      return;
    }
    mimeTypeRef.current = mime;

    // New operation: bump generation so any prior in-flight result is stale.
    const gen = (generationRef.current += 1);
    const capturedKey = contextKeyRef.current;
    recordStartKeyRef.current = capturedKey;
    const startedAt = Date.now();
    dispatch({ type: 'START', contextKey: capturedKey, startedAt });
    setElapsed(0);
    chunksRef.current = [];

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        // Cancelled / context-changed / unmounted during the permission prompt.
        if (isResultStale(gen, generationRef.current)) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const recorder = new MediaRecorder(stream, { mimeType: mime });
        mediaRecorderRef.current = recorder;
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunksRef.current.push(e.data);
        };
        recorder.start();
        timerRef.current = setInterval(() => setElapsed((p) => p + 1), 1000);
        autoStopRef.current = setTimeout(() => {
          safeLog('max_duration_reached');
          stopRef.current();
        }, MAX_RECORDING_DURATION_S * 1000);
        safeLog('recording_started');
      })
      .catch(() => {
        if (isResultStale(gen, generationRef.current)) return; // already torn down
        dispatch({ type: 'RECORDING_FAILED' });
        safeLog('recording_failed');
        releaseResources();
      });
  }, [supported, releaseResources]);

  const transcribe = useCallback(
    async (blob: Blob, mime: string, gen: number, capturedKey: string) => {
      const controller = new AbortController();
      abortRef.current = controller;
      safeLog('transcription_started', { bytes: blob.size });
      try {
        const audioBase64 = await blobToBase64(blob);
        if (isResultStale(gen, generationRef.current)) return; // cancelled mid-encode
        const result = await transcribeAudioBlob(
          { audioBase64, mimeType: mime, mode: DICTATION_MODE, signal: controller.signal },
          { endpointUrl: VTT_URL, getToken: getIdToken },
        );
        abortRef.current = null;
        // Post-await guards: superseded generation OR the context moved on.
        if (
          isResultStale(gen, generationRef.current) ||
          contextChanged(capturedKey, contextKeyRef.current)
        ) {
          return;
        }
        const text = (result.text ?? '').trim();
        if (!text) {
          dispatch({ type: 'NO_SPEECH' });
          return;
        }
        onInsertRef.current(text);
        dispatch({ type: 'SUCCESS' });
        safeLog('transcription_completed');
      } catch {
        abortRef.current = null;
        // Our own abort (cancel/context-change/unmount) is not a user-facing error.
        if (isResultStale(gen, generationRef.current)) return;
        dispatch({ type: 'FAILURE' });
        safeLog('transcription_failed');
      }
    },
    [],
  );

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (autoStopRef.current) {
      clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== 'recording') {
      // Nothing captured (e.g. Stop during the permission prompt) — no backend call.
      invalidate();
      dispatch({ type: 'CANCEL' });
      return;
    }

    const gen = generationRef.current;
    const capturedKey = recordStartKeyRef.current;
    const mime = mimeTypeRef.current;
    dispatch({ type: 'STOP' });
    safeLog('recording_stopped');

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mime });
      // Release the mic + drop chunks immediately; the blob is a local temporary.
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      mediaRecorderRef.current = null;
      chunksRef.current = [];

      // A stale generation means Cancel/context-change/unmount already handled UI.
      if (isResultStale(gen, generationRef.current)) return;

      if (blob.size === 0) {
        dispatch({ type: 'NO_SPEECH' });
        return;
      }
      void transcribe(blob, mime, gen, capturedKey);
    };
    recorder.stop();
  }, [invalidate, transcribe]);

  const cancel = useCallback(() => {
    invalidate();
    dispatch({ type: 'CANCEL' });
    safeLog('recording_cancelled');
  }, [invalidate]);

  const dismissError = useCallback(() => {
    dispatch({ type: 'DISMISS_ERROR' });
  }, []);

  stopRef.current = stop;

  // ── Context change → cancel any live operation (PRD.279 FR-6) ──
  const prevContextKeyRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevContextKeyRef.current;
    prevContextKeyRef.current = args.contextKey;
    if (prev === undefined || prev === args.contextKey) return; // mount / same-context rerender
    const st = stateRef.current.status;
    if (st === 'recording' || st === 'transcribing') {
      invalidate();
      dispatch({ type: 'CONTEXT_CHANGED' });
      safeLog('context_changed_cancelled');
    }
  }, [args.contextKey, invalidate]);

  // ── Unmount (covers drawer close, which unmounts the composer) ──
  useEffect(() => () => invalidate(), [invalidate]);

  const canStart = useMemo(
    () => canStartDictation({ supported, configured, sending: args.sending, status: state.status }),
    [supported, configured, args.sending, state.status],
  );

  return {
    status: state.status,
    error: state.status === 'error' ? state.message : null,
    elapsed,
    maxDuration: MAX_RECORDING_DURATION_S,
    supported,
    available,
    canStart,
    start,
    stop,
    cancel,
    dismissError,
  };
}
